// Posts index: client-side filtering by search text and category. Runs only
// on pages that embed a #posts-data payload alongside a #post-grid.
//
// Deliberately separate from src/agents.js rather than reusing initAgents:
// articles have no type, no topics, and no prompt, so feeding them through the
// agent payload shape would arm the Markdown download with empty code fences
// and make #agents-data mean "posts" on this page.

const initPosts = () => {
    const dataEl = document.getElementById("posts-data");
    const grid = document.getElementById("post-grid");
    if (!dataEl || !grid) {
        return;
    }

    let posts = [];
    try {
        posts = JSON.parse(dataEl.textContent || "[]");
    } catch (err) {
        console.error("Failed to parse posts data:", err);
        return;
    }

    const bySlug = new Map(posts.map((post) => [post.slug, post]));
    const searchInput = document.getElementById("post-search");
    const catSelect = document.getElementById("post-cat");
    const emptyEl = document.getElementById("post-empty");
    const cards = Array.from(grid.querySelectorAll(".post-card"));

    let query = "";
    // null means "all". A Set so one shelf link can carry several categories,
    // e.g. /posts/?cat=Sales%20Engineering,Leadership
    let cats = null;

    const parseCats = (raw) => {
        if (!raw || raw === "all") {
            return null;
        }
        const wanted = new Set(
            raw
                .split(",")
                .map((cat) => cat.trim())
                .filter(Boolean)
        );
        return wanted.size ? wanted : null;
    };

    const matches = (card) => {
        if (cats && !cats.has(card.dataset.cat)) {
            return false;
        }
        if (query) {
            const post = bySlug.get(card.dataset.slug) || {};
            const haystack = [post.title, post.desc, post.cat]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            if (!haystack.includes(query)) {
                return false;
            }
        }
        return true;
    };

    const apply = () => {
        let visible = 0;
        cards.forEach((card) => {
            const show = matches(card);
            card.style.display = show ? "" : "none";
            if (show) {
                visible += 1;
            }
        });
        if (emptyEl) {
            emptyEl.hidden = visible !== 0;
        }
    };

    // The <select> holds one category at a time. A multi-category URL shows as
    // "all" there while still filtering, which is honest: the control can't
    // represent that state.
    const syncSelect = () => {
        if (!catSelect) {
            return;
        }
        const single = cats && cats.size === 1 ? [...cats][0] : "all";
        catSelect.value = [...catSelect.options].some(
            (option) => option.value === single
        )
            ? single
            : "all";
        catSelect.parentElement.classList.toggle("is-active", Boolean(cats));
    };

    const pushUrl = () => {
        const url = cats
            ? `?cat=${encodeURIComponent([...cats].join(","))}`
            : location.pathname;
        history.pushState(null, "", url);
    };

    if (searchInput) {
        searchInput.addEventListener("input", (event) => {
            query = event.target.value.trim().toLowerCase();
            apply();
        });
    }

    if (catSelect) {
        catSelect.addEventListener("change", (event) => {
            cats = parseCats(event.target.value);
            syncSelect();
            pushUrl();
            apply();
        });
    }

    // Clicking a category chip on a card filters in place. The attribute is
    // `data-post-cat`, not `data-cat`, so the agents gallery's document-level
    // handler for a[data-cat] can never claim these links.
    document.addEventListener("click", (event) => {
        const link = event.target.closest("a[data-post-cat]");
        if (!link) {
            return;
        }
        event.preventDefault();
        cats = parseCats(link.dataset.postCat);
        syncSelect();
        pushUrl();
        apply();
    });

    cats = parseCats(new URLSearchParams(location.search).get("cat"));
    syncSelect();
    apply();
};

export { initPosts };
